import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="w-12 h-12 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#FDFCF8] min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} max-w-3xl`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {t.services.subtitle}
            </p>
            <h1
              data-testid="services-title"
              className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-6xl font-medium text-[#1A1A1A] mb-6`}
            >
              {t.services.title}
            </h1>
            <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-lg text-[#666660] leading-relaxed`}>
              {language === 'en'
                ? 'Experience premium beauty services performed by certified professionals using the finest products and techniques.'
                : 'استمتعي بخدمات تجميل متميزة يقدمها محترفون معتمدون باستخدام أفضل المنتجات والتقنيات.'}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card
                key={service.id}
                data-testid={`service-card-${index}`}
                className="group relative overflow-hidden bg-white border border-[#E6E2D6]/50 hover:border-[#C5A059]/30 transition-all duration-500 hover:shadow-2xl hover:shadow-[#C5A059]/5"
              >
                {/* Image */}
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={service.image_url}
                    alt={language === 'en' ? service.name_en : service.name_ar}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>

                <CardContent className={`p-8 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {/* Title */}
                  <h3 className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-2xl font-medium text-[#1A1A1A] mb-4`}>
                    {language === 'en' ? service.name_en : service.name_ar}
                  </h3>

                  {/* Description */}
                  <p className={`${isRTL ? 'font-tajawal' : 'font-manrope'} text-[#666660] leading-relaxed mb-6`}>
                    {language === 'en' ? service.description_en : service.description_ar}
                  </p>

                  {/* Duration & Price */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Clock className="w-4 h-4 text-[#C5A059]" />
                      <span className="font-manrope text-sm text-[#666660]">
                        {service.duration}
                      </span>
                    </div>
                    <p className="font-playfair text-2xl font-medium text-[#C5A059]">
                      {service.price}
                    </p>
                  </div>

                  {/* CTA */}
                  <Link to="/contact">
                    <Button
                      data-testid={`book-service-${index}`}
                      className="w-full rounded-full bg-[#C5A059] text-white hover:bg-[#B08A3E] py-6 text-base font-medium shadow-[0_4px_14px_0_rgba(197,160,89,0.39)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.23)] hover:-translate-y-1 transition-all duration-300"
                    >
                      {t.services.bookNow}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
